import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Clock } from 'lucide-react';
import { serviceCallsApi, TeamCallInfo, TeamDayDetail } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { dur, todayIST, STATE_STYLE } from './ServiceTeam';

/**
 * One service person's day on its own page, from check-in to check-out:
 * every stretch with its from and to time — busy on which call, or free — and
 * for each free stretch whether any call was waiting for allocation at the
 * same time, which ones, and how long nobody took them.
 */

type Board = NonNullable<Awaited<ReturnType<typeof serviceCallsApi.getTeamBoard>>['data']>;

const CallLine: React.FC<{ c: TeamCallInfo; at: (v: string) => string }> = ({ c, at }) => (
  <div>
    <div>
      <span className="font-semibold text-gray-900">#{c.id}</span>{' '}
      <span className="font-medium">{c.customer || '—'}</span>
      {c.service_type ? <span className="text-gray-500"> · {c.service_type}</span> : null}
      <span className="ml-1.5 px-1.5 py-0.5 rounded bg-gray-100 text-[10px] text-gray-600">{c.status}</span>
    </div>
    {(c.contact || c.mobile) && (
      <div className="text-gray-500">{[c.contact, c.mobile].filter(Boolean).join(' · ')}</div>
    )}
    <div className="text-gray-500">
      Logged {at(c.logged_at)}
      {c.taken_at ? <> · taken {at(c.taken_at)}{c.taken_by ? ` by ${c.taken_by}` : ''}</> : ' · not taken yet'}
      {c.closed_at ? <> · closed {at(c.closed_at)}</> : null}
    </div>
    {c.remark && <div className="text-gray-400 italic truncate max-w-[520px]" title={c.remark}>{c.remark}</div>}
  </div>
);

const ServiceTeamDay: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [params, setParams] = useSearchParams();
  const userId = params.get('user') || '';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(params.get('date') || '') ? params.get('date')! : todayIST();
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await serviceCallsApi.getTeamBoard(date, userId);
      setBoard(res.data);
    } catch (e: any) {
      showError('Could not load the day', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, userId]);

  useEffect(() => { load(); }, [load]);

  // Today is live: refresh every minute.
  useEffect(() => {
    if (!board?.is_today) return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [board?.is_today, load]);

  const set = (next: { user?: string; date?: string }) =>
    setParams({ user: next.user ?? userId, date: next.date ?? date }, { replace: true });

  const detail: TeamDayDetail | null | undefined = board?.detail;
  const row = board?.users.find(u => u.user_id === userId);
  // "2026-05-02 11:08" → "11:08" on this day, "05-05 10:33" on another.
  const at = (v: string) => (v.slice(0, 10) === date ? v.slice(11) : `${v.slice(8, 10)}-${v.slice(5, 7)} ${v.slice(11)}`);

  const segs = detail?.segments || [];
  const busyMin = segs.filter(s => s.kind === 'busy').reduce((n, s) => n + s.minutes, 0);
  const freeSegs = segs.filter(s => s.kind === 'free');
  const freeMin = freeSegs.reduce((n, s) => n + s.minutes, 0);
  const freeWithWork = freeSegs.filter(s => s.waiting.length > 0);
  const idleMin = row?.idle_while_waiting_minutes ?? freeSegs.reduce((n, s) => n + s.idle_minutes, 0);

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 max-w-[1300px] mx-auto">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={() => navigate(`/service/team${date === todayIST() ? '' : `?date=${date}`}`)}
            className="p-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-50" title="Back to the team board"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <Clock className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-800">Day timeline</h1>
          <div className="flex-1" />
          <select
            value={userId}
            onChange={e => set({ user: e.target.value })}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white"
          >
            {!row && <option value={userId}>{detail?.name || 'Select a person'}</option>}
            {(board?.users || []).map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
          <input
            type="date"
            value={date}
            max={todayIST()}
            onChange={e => set({ date: e.target.value || todayIST() })}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded"
          />
          <button onClick={load} disabled={loading} className="p-2 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {!detail ? (
          <div className="py-12 text-center text-sm text-gray-400">{loading ? 'Loading the day…' : 'Pick a person from the team board.'}</div>
        ) : !detail.checked_in ? (
          <div className="py-12 text-center text-sm text-gray-500">{detail.name} did not check in on this day, so there is no working time to show.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="rounded-lg border p-3 bg-gray-50 border-gray-200">
                <div className="text-[11px] font-bold uppercase text-gray-500">Shift</div>
                <div className="text-lg font-bold text-gray-800 tabular-nums">
                  {detail.checked_in.slice(11)} – {detail.checked_out ? detail.checked_out.slice(11) : board?.is_today && !detail.shift_end_assumed ? 'now' : '?'}
                </div>
                <div className="text-[11px] text-gray-500">
                  {detail.checked_out ? 'check-in to check-out' : detail.shift_end_assumed ? 'no check-out — nine hours assumed' : 'still on shift'}
                </div>
              </div>
              <div className="rounded-lg border p-3 bg-amber-50 border-amber-200">
                <div className="text-[11px] font-bold uppercase text-gray-500">Busy</div>
                <div className="text-lg font-bold text-amber-700">{dur(busyMin)}</div>
                <div className="text-[11px] text-gray-500">working on a call</div>
              </div>
              <div className="rounded-lg border p-3 bg-emerald-50 border-emerald-200">
                <div className="text-[11px] font-bold uppercase text-gray-500">Free</div>
                <div className="text-lg font-bold text-emerald-700">{dur(freeMin)}</div>
                <div className="text-[11px] text-gray-500">{freeSegs.length} stretch{freeSegs.length === 1 ? '' : 'es'}</div>
              </div>
              <div className={`rounded-lg border p-3 ${idleMin ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="text-[11px] font-bold uppercase text-gray-500">Free while calls pending</div>
                <div className={`text-lg font-bold ${idleMin >= 30 ? 'text-red-600' : 'text-gray-800'}`}>{dur(idleMin)}</div>
                <div className="text-[11px] text-gray-500">in {freeWithWork.length} free stretch{freeWithWork.length === 1 ? '' : 'es'}</div>
              </div>
              <div className={`rounded-lg border p-3 ${detail.waiting_calls.length ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="text-[11px] font-bold uppercase text-gray-500">Calls left waiting</div>
                <div className={`text-lg font-bold ${detail.waiting_calls.length ? 'text-red-600' : 'text-gray-800'}`}>{detail.waiting_calls.length}</div>
                <div className="text-[11px] text-gray-500">pending allocation while free</div>
              </div>
            </div>

            <div className="border border-gray-200 rounded overflow-auto max-h-[calc(100vh-260px)]">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-left">From</th>
                    <th className="px-2 py-2 text-left">To</th>
                    <th className="px-2 py-2 text-right">Duration</th>
                    <th className="px-2 py-2 text-left">Status</th>
                    <th className="px-2 py-2 text-left">Service call details</th>
                    <th className="px-2 py-2 text-left">Pending allocation at that time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {segs.map((s, i) => (
                    <tr key={i} className={s.kind === 'free' ? (s.waiting.length ? 'bg-red-50/60' : 'bg-emerald-50/30') : ''}>
                      <td className="px-2 py-2 align-top tabular-nums font-semibold whitespace-nowrap">{s.from}</td>
                      <td className="px-2 py-2 align-top tabular-nums font-semibold whitespace-nowrap">{s.to}</td>
                      <td className="px-2 py-2 align-top tabular-nums text-right whitespace-nowrap">{dur(s.minutes)}</td>
                      <td className="px-2 py-2 align-top">
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase ${STATE_STYLE[s.kind]}`}>{s.kind}</span>
                      </td>
                      <td className="px-2 py-2 align-top">
                        {s.kind === 'busy'
                          ? <div className="space-y-2">{s.calls.map(c => <CallLine key={c.id} c={c} at={at} />)}</div>
                          : <span className="text-gray-400">Free — no call in progress</span>}
                      </td>
                      <td className="px-2 py-2 align-top">
                        {s.kind === 'busy' ? (
                          <span className="text-gray-300">—</span>
                        ) : s.waiting.length === 0 ? (
                          <span className="font-semibold text-emerald-700">No — nothing was waiting</span>
                        ) : (
                          <div className="space-y-2">
                            <div className="font-semibold text-red-700">
                              Yes — {s.waiting.length} call{s.waiting.length === 1 ? '' : 's'} waiting · {dur(s.idle_minutes)} of this stretch with work available
                            </div>
                            {s.waiting.map(w => (
                              <div key={w.id} className="pl-2 border-l-2 border-red-200">
                                <CallLine c={w} at={at} />
                                <div className="text-red-700">
                                  Unallocated for <strong>{dur(w.unallocated_minutes)}</strong> · {dur(w.overlap_minutes)} of it during this free stretch
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {detail.waiting_calls.length > 0 && (
              <div className="mt-4 border border-red-200 rounded">
                <div className="px-3 py-2 text-[11px] font-bold uppercase text-red-700 bg-red-50 border-b border-red-200">
                  Calls pending allocation while {detail.name} was free ({detail.waiting_calls.length})
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Call</th>
                        <th className="px-2 py-1.5 text-left">Customer</th>
                        <th className="px-2 py-1.5 text-left">Contact</th>
                        <th className="px-2 py-1.5 text-left">Logged</th>
                        <th className="px-2 py-1.5 text-left">Taken</th>
                        <th className="px-2 py-1.5 text-right">Unallocated for</th>
                        <th className="px-2 py-1.5 text-right">While free</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detail.waiting_calls.map(w => (
                        <tr key={w.id}>
                          <td className="px-2 py-1.5 font-semibold">#{w.id}</td>
                          <td className="px-2 py-1.5">{w.customer}{w.service_type ? <span className="text-gray-400"> · {w.service_type}</span> : null}</td>
                          <td className="px-2 py-1.5 text-gray-600">{[w.contact, w.mobile].filter(Boolean).join(' · ')}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{at(w.logged_at)}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {w.taken_at ? <>{at(w.taken_at)} <span className="text-gray-500">{w.taken_by}</span></> : <span className="text-red-600">not taken</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{dur(w.unallocated_minutes)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-red-600">{dur(w.overlap_minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
              Times run from check-in to check-out. A call on hold does not count as busy. Calls picked up within a minute,
              and gaps under a minute between two calls, are left out. A date is shown when a call was logged or taken on another day.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceTeamDay;
